import { useCallback, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Chip } from '../../components/Chip';
import { EmptyState } from '../../components/EmptyState';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { LIST_MAX_HEIGHT, Table, type Column } from '../../components/Table';
import { copyToClipboard, errorMessage, useLoad } from '../members/hooks';
import { IpVaultPanel } from './IpVaultPanel';
import { SecurityBlockPanel } from './SecurityBlockPanel';
import { currentMonth, type Org, type OrgUsage } from './types';

interface Row extends Org {
  art: { total: number; fromPool: number; generated: number };
}

export function OrgStatusChip({ status }: { status: string }) {
  if (status === 'active') return <Chip tone="success">Activa</Chip>;
  if (status === 'blocked') return <Chip tone="error">Bloqueada</Chip>;
  return <Chip>{status}</Chip>;
}

export function OrgsPage() {
  const [month, setMonth] = useState(currentMonth());
  const loader = useCallback(async () => {
    const [orgs, usage] = await Promise.all([
      api.get<{ items: Org[] }>('/v1/orgs'),
      api.get<OrgUsage>(`/v1/operator/orgs/usage?month=${month}`),
    ]);
    const byOrg = new Map(usage.items.map((u) => [u.orgId, u.qrArt]));
    const rows: Row[] = orgs.items.map((o) => ({ ...o, art: byOrg.get(o.id) ?? { total: 0, fromPool: 0, generated: 0 } }));
    return { rows, source: usage.source };
  }, [month]);
  const { data, error, loading, reload } = useLoad(loader);
  const [createOpen, setCreateOpen] = useState(false);
  const closeCreate = useCallback(() => setCreateOpen(false), []);
  const [notice, setNotice] = useState<string | null>(null);

  const columns: Column<Row>[] = [
    { key: 'name', header: 'Organización', render: (o) => <Link to={`/operador/organizaciones/${encodeURIComponent(o.id)}`}>{o.name ?? o.id}</Link> },
    { key: 'slug', header: 'Identificador', mono: true, render: (o) => o.slug },
    { key: 'type', header: 'Tipo', render: (o) => o.type },
    {
      key: 'art',
      header: 'QR Art del mes (pool / generado)',
      align: 'right',
      render: (o) => (
        <span className="tabular-nums">
          {o.art.total} <span className="text-ink-muted">({o.art.fromPool} / {o.art.generated})</span>
        </span>
      ),
    },
    { key: 'status', header: 'Estado', render: (o) => <OrgStatusChip status={o.status} /> },
  ];

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold text-ink">Organizaciones</h1>
          <p className="text-sm text-ink-muted">Partners de la plataforma y su consumo de QR Art.</p>
        </div>
        <div className="flex items-end gap-3">
          <div className="w-40">
            <Input label="Mes" type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
          </div>
          <Button onClick={() => setCreateOpen(true)}>Crear organización partner</Button>
        </div>
      </div>

      <div aria-live="polite">{notice && <Banner tone="success">{notice}</Banner>}</div>
      {error && <Banner tone="error" action={<Button variant="secondary" size="sm" onClick={() => void reload()}>Reintentar</Button>}>{error}</Banner>}

      <div className="space-y-2">
        <Table
          caption="Organizaciones y consumo de QR Art"
          columns={columns}
          rows={data?.rows ?? []}
          rowKey={(o) => o.id}
          loading={loading && !data}
          stickyHeader
          maxHeight={LIST_MAX_HEIGHT}
          empty={<EmptyState title="Sin organizaciones" description="Crea la primera organización partner." />}
        />
        {data?.source === 'slots_scan' && (
          <p className="text-xs text-ink-muted">Consumo calculado desde los QR del mes: aún no hay agregados mensuales.</p>
        )}
        <p className="text-xs text-ink-muted">Los lotes no requieren aprobación del operador: el tope de 5.000 por lote es el control.</p>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <SecurityBlockPanel />
        <IpVaultPanel />
      </div>

      <CreateOrgModal
        open={createOpen}
        onClose={closeCreate}
        onCreated={async (name) => {
          setNotice(`Creamos ${name} e invitamos a su Owner.`);
          await reload();
        }}
      />
    </section>
  );
}

function CreateOrgModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (name: string) => Promise<void> }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [error, setError] = useState<{ field?: 'slug'; text: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [acceptUrl, setAcceptUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const close = useCallback(() => {
    setName('');
    setSlug('');
    setSlugTouched(false);
    setOwnerEmail('');
    setError(null);
    setAcceptUrl(null);
    setCopied(false);
    onClose();
  }, [onClose]);

  function suggestSlug(value: string) {
    return value
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/.test(slug)) {
      setError({ field: 'slug', text: 'Usa de 3 a 40 letras minúsculas, números o guiones, sin guion al inicio ni al final.' });
      return;
    }
    setLoading(true);
    try {
      const res = await api.post<{ org: Org; invitation: { acceptUrl?: string } }>('/v1/orgs', { name: name.trim(), slug, ownerEmail: ownerEmail.trim() });
      await onCreated(res.org.name ?? res.org.id);
      if (res.invitation?.acceptUrl) setAcceptUrl(res.invitation.acceptUrl);
      else close();
    } catch (err) {
      const text = errorMessage(err);
      setError(text.includes('identificador') ? { field: 'slug', text } : { text });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal open={open} onClose={close} title="Crear organización partner">
      {acceptUrl ? (
        <div className="space-y-3">
          <Banner tone="success">Organización creada. En este ambiente no se envía el correo al Owner; comparte este enlace:</Banner>
          <code className="block break-all rounded-sm bg-mist px-2 py-1 font-mono text-xs">{acceptUrl}</code>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={async () => setCopied(await copyToClipboard(acceptUrl))}>{copied ? 'Copiado' : 'Copiar enlace'}</Button>
            <Button onClick={close}>Listo</Button>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <Input
            label="Nombre"
            value={name}
            maxLength={80}
            onChange={(e) => {
              setName(e.target.value);
              if (!slugTouched) setSlug(suggestSlug(e.target.value));
            }}
          />
          <Input
            label="Identificador"
            mono
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value.toLowerCase());
            }}
            hint="No se puede cambiar después."
            error={error?.field === 'slug' ? error.text : undefined}
            className="normal-case"
          />
          <Input label="Email del Owner" type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} />
          {error && !error.field && <Banner tone="error">{error.text}</Banner>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={close}>Cancelar</Button>
            <Button type="submit" loading={loading} disabled={!name.trim() || !ownerEmail.trim()}>Crear e invitar Owner</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
